import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { useEffect, useState } from 'react';

import { useAuth, useDb, useEnsureAuth } from '../firebase/FirebaseContext';
import { useRecipients } from './useRecipients';
import { giftActivityCollectionPath } from '../schemas/paths';

export interface TotalLikedCountState {
  total: number;
  /**
   * True once the count is ANSWERABLE — recipients have hydrated AND every
   * recipient's `giftActivity` listener has delivered a first snapshot (or
   * there are no recipients to ask about, which is a real zero).
   *
   * ADDITIVE sibling of `useTotalLikedCount()`, which still returns the bare
   * number so no existing consumer changes. The Hub's You tab needs it because
   * `total === 0` cannot tell "nothing saved" from "the per-recipient fan-out
   * has not answered yet" — and the fan-out can only START after recipients
   * hydrate, so `useRecipients().hydrated` alone is NOT a proxy for it. That
   * gap is exactly what painted "Nothing saved yet — tap a heart to start a
   * board." at an account that has saves.
   */
  hydrated: boolean;
}

// Aggregate count of giftActivity entries with state='SAVED' across every
// recipient the user owns. Drives the sign-in nudge: once the count
// crosses the threshold (3+), the avatar shows a red dot and the menu
// surfaces a "save your boards" alert until the user signs in.
//
// Implementation: per-recipient onSnapshot subscriptions, summed
// client-side. We considered a `collectionGroup('giftActivity')` query
// filtered by uid in the path, but that requires a Firestore composite
// index we don't have today and most users have <5 recipients — the
// per-recipient fan-out cost is negligible for the realistic N.
/** The count plus whether it can be believed yet. See `TotalLikedCountState`. */
export function useTotalLikedCountState(): TotalLikedCountState {
  const auth = useAuth();
  const db = useDb();
  const ensureAuth = useEnsureAuth();
  const [uid, setUid] = useState<string | null>(auth.currentUser?.uid ?? null);
  const { recipients, hydrated: recipientsHydrated } = useRecipients();
  const [perRecipientCounts, setPerRecipientCounts] = useState<Record<string, number>>({});

  useEffect(() => {
    return onAuthStateChanged(auth, (user) => {
      const nextUid = user?.uid ?? null;
      setUid((prev) => {
        if (prev === nextUid) return prev;
        setPerRecipientCounts({});
        return nextUid;
      });
    });
  }, [auth]);

  useEffect(() => {
    let cancelled = false;
    const unsubscribers: Array<() => void> = [];

    if (recipients.length === 0) {
      // Drop any lingering counts when the recipient list empties (sign-out
      // or merge-cascade-delete) so the nudge resets cleanly.
      setPerRecipientCounts({});
      return () => undefined;
    }

    // Defensive: same guard as `useRecipients` for legacy tests that mock
    // `firebaseConfig` without `ensureAuth`. Without this, surfaces that
    // incidentally render HeaderAccountMenu (SiteHeader, ResultsHeader)
    // would crash with "ensureAuth is not a function".
    if (typeof ensureAuth !== 'function') {
      return () => undefined;
    }

    // Bind the identity this effect run was SCHEDULED for, not whatever
    // `ensureAuth()` happens to resolve. During an anon→permanent transition
    // `ensureAuth()` can hand back the OLD uid a beat late (a stale in-flight
    // anon sign-in, or a separate hook instance); `uid` from the authoritative
    // `onAuthStateChanged` is this run's real identity (a newer one would have
    // cancelled it). `resolvedUid` is used ONLY to bootstrap the null case
    // (cold load / fresh anon mint). Captured once, it targets the whole
    // per-recipient fan-out below, so a settled account is never stranded.
    const expectedUid = uid;

    ensureAuth()
      .then((resolvedUid) => {
        if (cancelled) return;
        // Known identity → bind it directly; `resolvedUid` bootstraps null.
        const targetUid = expectedUid ?? resolvedUid;
        recipients.forEach((r) => {
          const ref = collection(db, ...giftActivityCollectionPath(targetUid, r.recipientId));
          // `where('state', '==', 'SAVED')` keeps the count tight — DISMISSED /
          // PURCHASED entries don't count toward the sign-in nudge, only
          // explicit likes do.
          const q = query(ref, where('state', '==', 'SAVED'));
          const unsub = onSnapshot(
            q,
            (snap) => {
              if (cancelled) return;
              setPerRecipientCounts((prev) => {
                if (prev[r.recipientId] === snap.size) return prev;
                return { ...prev, [r.recipientId]: snap.size };
              });
            },
            () => {
              // Silent on error — the nudge is opportunistic, not critical.
            },
          );
          unsubscribers.push(unsub);
        });
      })
      .catch(() => {
        // Ditto — silent.
      });

    return () => {
      cancelled = true;
      unsubscribers.forEach((u) => u());
    };
  }, [uid, recipients, db, ensureAuth]);

  // Sum across every recipient. Keys for archived/removed recipients are
  // pruned by the recipients-effect's setPerRecipientCounts({}) reset on
  // uid swap, so stale entries can't leak across sessions.
  const total = Object.values(perRecipientCounts).reduce((acc, n) => acc + n, 0);

  // `typeof ensureAuth !== 'function'` is the legacy-mock escape hatch the
  // effect above already honours by subscribing to nothing. Reporting
  // un-hydrated forever there would hold a consumer's shimmer up on a case
  // that is never going to answer, so it reports hydrated: there is nothing
  // left to wait for, which is the honest reading of "no subscriptions".
  const hydrated =
    recipientsHydrated &&
    (typeof ensureAuth !== 'function' ||
      recipients.every((r) => perRecipientCounts[r.recipientId] !== undefined));

  return { total, hydrated };
}

/**
 * The count on its own — the shape every pre-Hub caller uses. Kept as the
 * default export-shaped hook rather than making them all destructure, because
 * the sign-in nudge genuinely does not care whether the number is final.
 */
export function useTotalLikedCount(): number {
  return useTotalLikedCountState().total;
}
